// src/modules/callSync/components/LeadCallHistory.jsx
// Call history for one lead (STEP 13).

import React, { useEffect, useState, useCallback } from 'react';
import { Timeline, Empty, Spin, Tag, Typography, Space, Tooltip, Button } from 'antd';
import { PhoneOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchLeadCalls } from '../api';
import { statusMeta, dispositionLabel, formatDuration, isMeasured } from '../constants';

const { Text } = Typography;

export default function LeadCallHistory({ leadId, refreshKey }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await fetchLeadCalls(leadId);
      setCalls(res.data.data || []);
    } catch (err) {
      // Call history is supplementary; a failure here must not break the lead view.
      setCalls([]);
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && !calls.length) return <Spin />;
  if (!calls.length) return <Empty description="No calls yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <>
      <div style={{ textAlign: 'right', marginBottom: 8 }}>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <Timeline
        items={calls.map((c) => {
          const meta = statusMeta(c.status);
          const measured = isMeasured(c);
          return {
            color: meta.color === 'success' ? 'green'
              : meta.color === 'error' ? 'red'
                : meta.color === 'warning' ? 'orange' : 'blue',
            children: (
              <div>
                <Space wrap size={4}>
                  <Text strong>{dayjs(c.createdAt).format('DD MMM YYYY hh:mm A')}</Text>
                  <Tag color={meta.color}>{meta.label}</Tag>
                  {c.origin === 'UNTRACKED_CALL' && (
                    <Tooltip title="Dialled directly on the phone, not from the CRM. Detected from the device call log.">
                      <Tag color="purple">Manual</Tag>
                    </Tooltip>
                  )}
                  {c.durationSeconds > 0 && (
                    <Tag color={measured ? 'green' : 'orange'}>
                      {formatDuration(c.durationSeconds)}
                      {!measured && (
                        <Tooltip title="Estimated from the phone's call state; may include ringing time.">
                          {' '}<InfoCircleOutlined />
                        </Tooltip>
                      )}
                    </Tag>
                  )}
                </Space>
                <div style={{ fontSize: 12, color: '#888' }}>
                  <PhoneOutlined /> {c.phoneNumber}
                  {c.salesRep?.name ? ` · ${c.salesRep.name}` : ''}
                </div>
                {c.disposition && <div><Text type="secondary">Outcome: </Text>{dispositionLabel(c.disposition)}</div>}
                {c.notes && <div>{c.notes}</div>}
                {c.followUpAt && (
                  <div style={{ fontSize: 12 }}>
                    Follow-up: {dayjs(c.followUpAt).format('DD MMM YYYY hh:mm A')}
                  </div>
                )}
                {c.failureReason && (
                  <div style={{ fontSize: 12, color: '#cf1322' }}>
                    {c.failureReason.replace(/_/g, ' ').toLowerCase()}
                  </div>
                )}
              </div>
            ),
          };
        })}
      />
    </>
  );
}
